import React, { useState, useEffect } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import CurrencySelect from "@/components/storefront/CurrencySelect";
import { TID } from "@/lib/testIds";

const Header = () => {
  const { settings, setSearchOpen, setBagOpen, bagCount, saved } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  const nav = settings?.nav || [];
  const promoted = settings?.promoted;
  const ann = settings?.announcement;
  const isHome = loc.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setMobileOpen(false), [loc.pathname]);

  const solid = scrolled || !isHome || mobileOpen;

  return (
    <>
      {ann?.enabled && (
        <Link to={ann.href || "/"} data-testid={TID.announcement}
          className="block bg-espresso text-cream text-center text-[0.68rem] tracking-[0.2em] uppercase py-2.5 px-4 font-medium">
          {ann.text}
        </Link>
      )}
      <header className={`sticky top-0 z-40 transition-colors duration-500 ${solid ? "bg-ivory/95 backdrop-blur-md border-b border-sand text-espresso" : "bg-transparent text-cream"}`}>
        <div className="max-w-[1600px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center h-16 md:h-20 gap-3">
            <div className="flex items-center gap-4 justify-start min-w-0">
              <button className="xl:hidden" onClick={() => setMobileOpen((v) => !v)} data-testid={TID.mobileMenu} aria-label="Menu">
                {mobileOpen ? <X strokeWidth={1.4} /> : <Menu strokeWidth={1.4} />}
              </button>
              <nav className="hidden xl:flex items-center gap-x-3 2xl:gap-x-5 text-[0.63rem] tracking-[0.1em] font-medium">
                {nav.map((n) => (
                  <NavLink key={n.href} to={n.href} data-testid={TID.navItem(n.href.split("/").pop())}
                    className="hover-underline py-1 whitespace-nowrap">{n.label}</NavLink>
                ))}
              </nav>
            </div>

            <Link to="/" data-testid={TID.logo}
              className="font-serif text-lg sm:text-xl md:text-[1.6rem] tracking-[0.08em] md:tracking-[0.12em] leading-none whitespace-nowrap text-center px-1">
              NOIRBYAMARA
            </Link>

            <div className="flex items-center gap-4 md:gap-5 justify-end min-w-0">
              <div className="hidden md:block"><CurrencySelect /></div>
              {promoted?.enabled && (
                <Link to={promoted.href} className="hidden md:inline-block text-[0.68rem] tracking-[0.18em] uppercase border border-current px-3 py-1.5 hover:bg-espresso hover:text-cream transition-colors">
                  {promoted.label}
                </Link>
              )}
              <button onClick={() => setSearchOpen(true)} data-testid={TID.searchOpen} aria-label="Search"><Search size={19} strokeWidth={1.4} /></button>
              <Link to="/account" data-testid={TID.account} aria-label="Account" className="hidden sm:inline-block"><User size={19} strokeWidth={1.4} /></Link>
              <Link to="/saved" data-testid={TID.saved} aria-label="Saved pieces" className="relative">
                <Heart size={19} strokeWidth={1.4} />
                {saved.length > 0 && <span className="absolute -top-2 -right-2 text-[0.6rem] bg-wine text-cream rounded-full w-4 h-4 flex items-center justify-center">{saved.length}</span>}
              </Link>
              <button onClick={() => setBagOpen(true)} data-testid={TID.bag} aria-label="Bag" className="relative">
                <ShoppingBag size={19} strokeWidth={1.4} />
                {bagCount > 0 && <span className="absolute -top-2 -right-2 text-[0.6rem] bg-espresso text-cream rounded-full w-4 h-4 flex items-center justify-center">{bagCount}</span>}
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <nav className="xl:hidden bg-ivory border-t border-sand px-5 py-6 flex flex-col gap-5 text-sm tracking-[0.14em] uppercase">
            {nav.map((n) => (
              <Link key={n.href} to={n.href} data-testid={`mobile-${TID.navItem(n.href.split("/").pop())}`}
                className="font-serif text-2xl normal-case tracking-normal">{n.label}</Link>
            ))}
            {promoted?.enabled && <Link to={promoted.href} className="text-wine">{promoted.label}</Link>}
            <div className="md:hidden pt-4 mt-1 border-t border-sand">
              <p className="text-[0.6rem] tracking-[0.24em] uppercase text-espresso/50 mb-3">Currency</p>
              <CurrencySelect />
            </div>
          </nav>
        )}
      </header>
    </>
  );
};

export default Header;
